'use client'

import lottie, { type AnimationItem } from 'lottie-web'
import Image from 'next/image'
import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useErrorHandling } from '@/tools/o11y/client'

interface JsonAnimationProps {
  autoplay?: boolean
  loop?: boolean
  jsonSrc?: string
  imgSrc?: string
  width?: number
  height?: number
  startAnimation?: boolean
  onAnimationEnd?: () => void
}

export const JsonAnimations: FC<JsonAnimationProps> = ({
  autoplay = true,
  loop = false,
  jsonSrc,
  imgSrc,
  width = 200,
  height = 200,
  onAnimationEnd,
}) => {
  const { withErrorTracking, execWithErrorTracking } = useErrorHandling()
  const animationContainer = useRef(null)
  const animationInstanceRef = useRef<AnimationItem>(null)
  const [animationLoaded] = useState<boolean>(false)

  useEffect(() => {
    execWithErrorTracking(() => {
      if (animationContainer.current) {
        const animationInstance = lottie.loadAnimation({
          container: animationContainer.current,
          renderer: 'svg',
          loop,
          autoplay,
          path: jsonSrc,
        })

        animationInstance.addEventListener('data_ready', () => {
          lottie.play()
        })

        animationInstance.addEventListener('complete', () => {
          onAnimationEnd?.()
        })

        animationInstanceRef.current = animationInstance
      }
    })

    return withErrorTracking(() => {
      lottie.destroy(animationInstanceRef.current?.name)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, loop, jsonSrc, animationLoaded, withErrorTracking, execWithErrorTracking])

  return (
    <div ref={animationContainer}>
      {imgSrc && !animationLoaded && (
        <Image alt="animation" height={height} src={imgSrc} width={width} />
      )}
    </div>
  )
}
